import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { AbstractCrudService } from 'src/app/shared/components/crud/abstract-crud.service';
import { API_PATH, IBGE_API_PATH } from 'src/environments/environment';
import { map, catchError } from 'rxjs/operators';
import { ToastService } from 'src/app/shared/services/toast/toast.service';

// Definir o enum ExportFormat aqui
export enum ExportFormat {
    PDF = 'pdf',
    EXCEL = 'xlsx',
    XML = 'xml'
}

export interface Contact {
    id: number;
    code?: string | number;
    name: string;
    email: string;
    phone?: string;
    cellPhone?: string;
    areaCode?: string;
    isCustomer: boolean;
    customerId?: string;
    customer?: any;
    isPartner: boolean;
    partnerId?: string;
    partner?: any;
    status: boolean;
    birthDate?: Date;
    cpf?: string;
    cnpj?: string;
    enterprise?: string;
    segment?: string;
    socialMedia?: string;
    role?: string;
    city?: string;
    state?: string;
    country?: string;
    webSite?: string;
    webSiteLink?: string;
    createdAt?: Date;
    updatedAt?: Date;
    customerName?: string; // Para exibição na tabela
    partnerName?: string; // Para exibição na tabela

    // Campos de Visibilidade
    isGlobal?: boolean;
    isInternal?: boolean;
    isShared?: boolean;
    sharedPartnerIds?: string[]; // Usado para enviar ao backend
    // Se o backend retornar os parceiros com quem foi compartilhado, poderíamos ter:
    // sharedWithPartners?: Partner[]; // Supondo uma interface Partner no frontend
}

export interface ContactImport {
    file: File;
}

export interface FilterOptions {
    phoneNumber?: string;
    phone?: string;
    cellPhone?: string;
    areaCode?: string;
    email?: string;
    isCustomer?: boolean;
    isPartner?: boolean;
    status?: boolean;
    customerId?: string;
    partnerId?: string;
    filter?: string;
    city?: string;
    state?: string;
}

export interface UrlResponse {
    url: string;
}

@Injectable({
    providedIn: 'root'
})
export class ContactsService extends AbstractCrudService {
    override title = 'Contato';
    override pluralTitle = 'Contatos';

    constructor(
        protected override httpClient: HttpClient,
        private toastService: ToastService
    ) {
        super(httpClient, 'contacts');
    }

    getAll(params?: any): Observable<[Contact[], number]> {
        const queryParams = params || { limit: 100, page: 0 };
        return this.httpClient.get<[Contact[], number]>(`${API_PATH}${this.relativeUrl}`, {
            params: queryParams
        });
    }
    
    getAllAdminView(params?: any): Observable<[Contact[], number]> {
        const queryParams = params || { limit: 100, page: 0 };
        return this.httpClient.get<[Contact[], number]>(`${API_PATH}${this.relativeUrl}/all`, {
            params: queryParams
        });
    }

    getFiltered(filterOptions: FilterOptions, paginationParams?: { page: number, limit: number }): Observable<[Contact[], number]> {
        const url = `${API_PATH}${this.relativeUrl}/filter`;

        // Os filterOptions já devem vir processados do componente.
        const paramsToSend: any = { ...filterOptions };

        if (paginationParams) {
            paramsToSend.page = paginationParams.page;
            paramsToSend.limit = paginationParams.limit;
        }

        // Remover chaves com valor undefined ou null.
        // Para campos especificamente booleanos, garantir que não sejam strings vazias.
        Object.keys(paramsToSend).forEach(key => {
            if (paramsToSend[key] === undefined || paramsToSend[key] === null) {
                delete paramsToSend[key];
            } else if (key === 'isCustomer' || key === 'isPartner' || key === 'status') {
                if (typeof paramsToSend[key] !== 'boolean') {
                    // Se não for um booleano após o tratamento no componente (que deveria ter removido se fosse null/undefined),
                    // e ainda assim não é booleano (ex: string vazia que escapou), removemos.
                    console.warn(`Campo booleano '${key}' com valor não booleano '${paramsToSend[key]}' sendo removido antes do envio.`);
                    delete paramsToSend[key];
                }
            }
        });

        console.log('Enviando filtros processados para a API:', paramsToSend);

        return this.httpClient.post<[Contact[], number]>(url, paramsToSend);
    }

    getById(id: string | number): Observable<Contact> {
        return this.httpClient.get<Contact>(`${API_PATH}${this.relativeUrl}/${id}/admin-view`);
    }

    override create(contact: any): Observable<any> {
        return this.httpClient.post<Contact>(`${API_PATH}${this.relativeUrl}`, contact);
    }

    override update(id: string | number, contact: any): Observable<any> {
        console.log('[ContactsService] Atualizando contato:', id, contact);
        // Verificar se contact é instância de FormData
        if (contact instanceof FormData) {
            // Já é FormData, apenas append o ID se não estiver presente
            if (!contact.has('id')) {
                contact.append('id', id.toString());
            }
            return this.httpClient.patch<Contact>(`${API_PATH}${this.relativeUrl}/${id}`, contact);
        } else {
            // É um objeto normal, fazer a conversão para FormData se necessário
            return this.httpClient.patch<Contact>(`${API_PATH}${this.relativeUrl}/${id}`, contact);
        }
    }

    override delete(id: string): Observable<void> {
        return this.httpClient.delete<void>(`${API_PATH}${this.relativeUrl}/${id}`);
    }

    deleteMany(ids: string[]): Observable<{ affected?: number }> {
        if (!ids || ids.length === 0) {
            return of({ affected: 0 }); // Retorna um observable com 0 afetados se não houver IDs
        }
        return this.httpClient.post<{ affected?: number }>(`${API_PATH}${this.relativeUrl}/batch-delete`, { ids });
    }

    deleteFiltered(filter: FilterOptions): Observable<{ affected?: number }> {
        console.warn('[ContactsService] Iniciando exclusão em lote baseada em filtros:', filter);
        // Garantir que booleanos sejam enviados corretamente
        const params = { ...filter };
        if (params.isCustomer === null || params.isCustomer === undefined) delete params.isCustomer;
        if (params.isPartner === null || params.isPartner === undefined) delete params.isPartner;
        if (params.status === null || params.status === undefined) delete params.status;

        return this.httpClient.post<{ affected?: number }>(`${API_PATH}${this.relativeUrl}/batch-delete-filtered`, params);
    }

    importContacts(data: FormData): Observable<any> {
        return this.httpClient.post<any>(`${API_PATH}${this.relativeUrl}/import`, data);
    }

    getImportStatus(): Observable<{ isImporting: boolean }> {
        return this.httpClient.get<{ isImporting: boolean }>(`${API_PATH}${this.relativeUrl}/import/status`);
    }

    private handleExportResponse(response: HttpResponse<Blob>, format: ExportFormat, selected: boolean): void {
        if (!response.body) {
            this.toastService.showError(`Resposta vazia ao exportar ${selected ? 'selecionados ' : ''}como ${format.toUpperCase()}.`);
            throw new Error('Empty response body');
        }
        const contentDisposition = response.headers.get('content-disposition');
        const filenameBase = selected ? `contatos_selecionados` : `contatos_todos`;
        let filename = `${filenameBase}.${format}`;
        if (contentDisposition) {
            const filenameRegex = /filename[^;=\n]*=((['"]?)(?:\\.|[^'"\n])*\2)/;
            const matches = filenameRegex.exec(contentDisposition);
            if (matches?.[1]) {
                filename = matches[1].replace(/['"]/g, '');
            }
        }

        const blob = new Blob([response.body], { type: response.headers.get('content-type') || undefined });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
    }

    private handleExportError(error: any, format: ExportFormat, selected: boolean): Observable<void> {
        console.error(`Erro ao exportar ${selected ? 'selecionados ' : ''}como ${format.toUpperCase()}:`, error);
        if (error.error instanceof Blob) {
            return new Observable<void>(observer => {
                const reader = new FileReader();
                reader.onload = (e: any) => {
                    try {
                        const err = JSON.parse(e.target.result);
                        this.toastService.showError(`Erro ao exportar: ${err.message || 'Erro desconhecido'}`);
                    } catch (jsonError) {
                        this.toastService.showError(`Erro ao exportar ${selected ? 'selecionados ' : ''}como ${format.toUpperCase()}. Resposta inválida.`);
                    }
                    observer.error(error); // Propagar erro original
                    observer.complete();
                };
                reader.onerror = () => {
                    this.toastService.showError('Erro ao ler a resposta de erro do servidor.');
                    observer.error(error);
                    observer.complete();
                };
                reader.readAsText(error.error);
            });
        } else {
            this.toastService.showError(`Erro ao exportar ${selected ? 'selecionados ' : ''}como ${format.toUpperCase()}. Tente novamente.`);
        }
        return of(); // Completar fluxo
    }

    // Exportar Todos
    exportAllToPdf(): Observable<void> {
        const url = `${API_PATH}${this.relativeUrl}/export/pdf`;
        return this.httpClient.get(url, { observe: 'response', responseType: 'blob' }).pipe(
            map(response => this.handleExportResponse(response, ExportFormat.PDF, false)),
            catchError(error => this.handleExportError(error, ExportFormat.PDF, false))
        );
    }

    exportAllToExcel(): Observable<void> {
        const url = `${API_PATH}${this.relativeUrl}/export/excel`;
        return this.httpClient.get(url, { observe: 'response', responseType: 'blob' }).pipe(
            map(response => this.handleExportResponse(response, ExportFormat.EXCEL, false)),
            catchError(error => this.handleExportError(error, ExportFormat.EXCEL, false))
        );
    }

    exportAllToXml(): Observable<void> {
        const url = `${API_PATH}${this.relativeUrl}/export/xml`;
        return this.httpClient.get(url, { observe: 'response', responseType: 'blob' }).pipe(
            map(response => this.handleExportResponse(response, ExportFormat.XML, false)),
            catchError(error => this.handleExportError(error, ExportFormat.XML, false))
        );
    }

    // Exportar Selecionados
    exportSelectedToPdf(contacts: Contact[]): Observable<void> {
        if (!contacts || contacts.length === 0) return of();

        // Filtrar contatos com IDs válidos e converter para string
        const ids = contacts
            .filter(c => c && c.id)
            .map(c => c.id.toString())
            .filter(id => id && id.trim() !== '');

        if (ids.length === 0) {
            this.toastService.showWarn('Nenhum contato válido selecionado para exportação PDF.');
            return of();
        }

        console.log('[Frontend Service] Exporting PDF for IDs:', ids);
        const url = `${API_PATH}${this.relativeUrl}/export/pdf/selected`;
        return this.httpClient.post(url, { ids }, { observe: 'response', responseType: 'blob' }).pipe(
            map(response => this.handleExportResponse(response, ExportFormat.PDF, true)),
            catchError(error => this.handleExportError(error, ExportFormat.PDF, true))
        );
    }

    exportSelectedToExcel(contacts: Contact[]): Observable<void> {
        if (!contacts || contacts.length === 0) return of();

        // Filtrar contatos com IDs válidos e converter para string
        const ids = contacts
            .filter(c => c && c.id)
            .map(c => c.id.toString())
            .filter(id => id && id.trim() !== '');

        if (ids.length === 0) {
            this.toastService.showWarn('Nenhum contato válido selecionado para exportação Excel.');
            return of();
        }

        console.log('[Frontend Service] Exporting Excel for IDs:', ids);
        const url = `${API_PATH}${this.relativeUrl}/export/excel/selected`;
        return this.httpClient.post(url, { ids }, { observe: 'response', responseType: 'blob' }).pipe(
            map(response => this.handleExportResponse(response, ExportFormat.EXCEL, true)),
            catchError(error => this.handleExportError(error, ExportFormat.EXCEL, true))
        );
    }

    exportSelectedToXml(contacts: Contact[]): Observable<void> {
        if (!contacts || contacts.length === 0) return of();

        // Filtrar contatos com IDs válidos e converter para string
        const ids = contacts
            .filter(c => c && c.id)
            .map(c => c.id.toString())
            .filter(id => id && id.trim() !== '');

        if (ids.length === 0) {
            this.toastService.showWarn('Nenhum contato válido selecionado para exportação XML.');
            return of();
        }

        console.log('[Frontend Service] Exporting XML for IDs:', ids);
        const url = `${API_PATH}${this.relativeUrl}/export/xml/selected`;
        return this.httpClient.post(url, { ids }, { observe: 'response', responseType: 'blob' }).pipe(
            map(response => this.handleExportResponse(response, ExportFormat.XML, true)),
            catchError(error => this.handleExportError(error, ExportFormat.XML, true))
        );
    }

    // Novo método para exportar template
    exportTemplate(): Observable<void> {
        const url = `${API_PATH}${this.relativeUrl}/export/template`;
        return this.httpClient.get(url, { observe: 'response', responseType: 'blob' }).pipe(
            // Reutilizando handleExportResponse, passando EXCEL como formato para o nome do arquivo
            map(response => this.handleExportResponse(response, ExportFormat.EXCEL, false)), // false = não é selecionado
            // Reutilizando handleExportError
            catchError(error => this.handleExportError(error, ExportFormat.EXCEL, false))
        );
    }

    getWhatsAppUrl(id: number): Observable<UrlResponse> {
        return this.httpClient.get<UrlResponse>(`${API_PATH}${this.relativeUrl}/${id}/whatsapp-url`);
    }

    getEmailUrl(id: number): Observable<UrlResponse> {
        return this.httpClient.get<UrlResponse>(`${API_PATH}${this.relativeUrl}/${id}/email-url`);
    }

    // Métodos específicos para formulários - não sobrescrever os da classe base
    createContactWithFormData(formData: FormData): Observable<Contact> {
        return this.httpClient.post<Contact>(`${API_PATH}${this.relativeUrl}/form-data`, formData);
    }

    updateContactWithFormData(formData: FormData): Observable<Contact> {
        const id = formData.get('id');
        console.log('[ContactsService] Atualizando contato com FormData, ID:', id);
        return this.httpClient.patch<Contact>(`${API_PATH}${this.relativeUrl}/${id}/form-data`, formData);
    }

    // Implementação da lista para o Abstract Crud Service
    override listAll(params: any): Observable<[Contact[], number]> {
        return this.getAll(params);
    }

    getBirthdayContacts(): Observable<Contact[]> {
        return this.httpClient.get<Contact[]>(`${API_PATH}${this.relativeUrl}/birthday`);
    }

    // Método para enviar mensagem de aniversário
    sendBirthdayMessage(contact: Contact): Observable<void> {
        return this.httpClient.post<void>(`${API_PATH}${this.relativeUrl}/${contact.id}/birthday-message`, contact);
    }

    // Método para alternar o status do contato
    toggleStatus(id: string): Observable<void> {
        return this.httpClient.patch<void>(`${API_PATH}${this.relativeUrl}/${id}/toggle-status`, {});
    }

    public getStates(): Observable<any[]> {
        return this.httpClient.get<any[]>(`${IBGE_API_PATH}localidades/estados`);
    }

    public getCitiesByUf(uf: string): Observable<any> {
        return this.httpClient.get(
            `${IBGE_API_PATH}localidades/estados/${uf}/municipios`
        );
    }

    /**
     * Adiciona uma tag a um contato
     * @param contactId ID do contato
     * @param tagName Nome da tag a ser adicionada
     * @returns Observable<any>
     */
    addTagToContact(contactId: string, tagName: string): Observable<any> {
        return this.httpClient.post<any>(`${API_PATH}${this.relativeUrl}/${contactId}/tags`, { tagName });
    }
} 